/**
 * Scan Pokédex modal — Push 5 / v1.4.2 b1 pivot.
 *
 * Lets the user pick one or more Pokémon GO Pokédex screenshots, runs the
 * new variant-aware classifier, ingests per-entry cell observations into
 * `dexStateStore`, and (for backward compatibility) also POSTs the
 * aggregate region/filter progress to the legacy /api/dex/pokedex-scan
 * endpoint.
 *
 * Key pivot rules enforced here:
 *   - The classifier identifies the active Dex variant from the bottom
 *     selector text first; the header card is supporting only.
 *   - Sparse variants (shadow/purified/mega/gmax) NEVER imply missing
 *     entries for numbers we didn't actually see in the frame.
 *   - The payload sent to the dexStateStore is Dex-state only — never
 *     CP, IV, instanceId, nickname or any other inventory field.
 *   - When no variant could be identified, the modal stays in review
 *     mode and asks the user to confirm — we never silently mis-tag.
 */
import { useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { X, Upload, Check, AlertTriangle, Crown, Sparkles, Zap } from "lucide-react";
import type { PokedexRegion, PokedexFilter } from "@/lib/pokedexOcr";
import {
  ALL_VARIANTS,
  isSparseVariant,
  type PokedexVariant,
} from "@/lib/pogoPokedexVariantClassifier";
import { previewDetailFrame } from "@/lib/pokemonDetailDexOcr";
import { speciesName } from "@/data/speciesNames";
import { useSession, hasProAccess } from "@/components/SessionProvider";
import { ProLock } from "@/components/ProLock";
import { formatScanProgress } from "@/lib/scanReviewDisplay";
import {
  classifyScreenshot,
  needsHumanReview,
  saveScreenshotRows,
  type DetailPending,
  type MyPokemonPending,
  type Pending,
} from "@/lib/screenshotScanPipeline";
import {
  clearHeldScreenshots,
  ensureScreenshotAccess,
  isScreenshotSweepAvailable,
  listHeldScreenshots,
  planSweep,
  readScreenshotFile,
  setSweepCursor,
} from "@/lib/screenshotLibrary";
import { dexNumberLabel } from "@/lib/dexNumberLabel";

interface ScanPokedexModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  /**
   * Pivot: the page that opens this modal knows which Dex variant the
   * user has selected; we use it as the *suggested* variant when the
   * classifier cannot read the bottom selector text reliably.
   */
  initialVariant?: PokedexVariant;
  /**
   * Fire the photo picker the moment the modal opens (native Photos sheet on
   * iOS/Android, file input on web), skipping the pick phase. "auto" saves
   * every clean read and closes itself (the Upload menu's web Auto Scan
   * fallback); "review" runs the review-first pipeline (Select screenshots).
   * Cancelling the picker closes the modal.
   */
  pickOnOpen?: "auto" | "review";
  /**
   * Open straight into reviewing the screenshots a background Auto Scan HELD
   * (uncertain variant / nickname / Pro lock): re-reads them from Photos by
   * asset id and runs the review-first pipeline. Saving clears the held
   * queue; cancelling keeps it for another look.
   */
  heldReviewOnOpen?: boolean;
}

type Phase = "pick" | "running" | "review" | "uploading" | "done";

// Pending row types live in screenshotScanPipeline (shared with the
// background Auto Scan runner).

// Short labels for the manual-confirmation variant picker.
const VARIANT_PICKER_LABELS: Record<PokedexVariant, string> = {
  pokemon: "Pokémon (main)",
  shiny: "Shiny",
  lucky: "Lucky",
  perfect: "Perfect (100%)",
  xxs: "XXS",
  xxl: "XXL",
  mega: "Mega",
  gmax: "G-Max",
  dynamax: "D-MAX",
  shadow: "Shadow",
  purified: "Purified",
  // pokedex-complete-dex: derived rollup, never a scan target (excluded from
  // ALL_VARIANTS, which drives the picker list) — label present only to satisfy
  // the exhaustive Record.
  complete: "Complete",
  shundo: "Shiny Perfect",
  shinyLucky: "Shiny Lucky",
  // Form dexes: manual-only, never a scan target — labels only for the Record.
  regional: "Regional",
  costume: "Costume",
};


export function ScanPokedexModal({ open, onClose, onConfirm, initialVariant, pickOnOpen, heldReviewOnOpen }: ScanPokedexModalProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { tier } = useSession();
  const isPro = hasProAccess(tier);
  const [phase, setPhase] = useState<Phase>("pick");
  const [pending, setPending] = useState<Pending[]>([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  // Photos in the picked batch that are not Pokémon GO screens at all (the
  // camera-roll case Auto Scan exists for). They are skipped, never parsed
  // into junk rows — but the count is surfaced so "487 photos in, 3 saved"
  // reads as the filter working rather than the scan losing screenshots.
  const [skippedPhotos, setSkippedPhotos] = useState(0);
  // Auto Scan (2026-08-21 ask, Poke Genie-style): pick a whole camera roll of
  // shots and save everything that parses cleanly in one go, no review stop.
  // A ref, not state — it must be current inside the async handleFiles that
  // the same tap kicks off.
  const autoRef = useRef(false);

  useEffect(() => {
    if (!open) {
      // Reset on close so the next session starts on the pick phase.
      setPhase("pick");
      setPending([]);
      setProgress({ done: 0, total: 0 });
      setSkippedPhotos(0);
      autoRef.current = false;
    }
  }, [open]);

  // pickOnOpen: fire the photo picker the moment the modal opens, once per
  // open. nativePicker.pickImages is the native Photos sheet on iOS/Android
  // and a synthetic file input on web (the same-tap transient activation
  // covers the programmatic click). Cancel → close the modal. "auto" saves
  // clean rows itself and closes (rows needing a human still stop in review);
  // "review" always stops in review first.
  const pickFiredRef = useRef(false);
  useEffect(() => {
    if (!open) {
      pickFiredRef.current = false;
      return;
    }
    if (pickFiredRef.current) return;
    if (pickOnOpen) {
      pickFiredRef.current = true;
      autoRef.current = pickOnOpen === "auto";
      void (async () => {
        // Resolve the photos permission on EVERY upload entry (Select
        // screenshots, the quick-add circle), not just Auto Scan: the picker
        // itself never needs it, but this is the moment the trainer expects
        // the ask, and granting here is what lets Auto Scan work later.
        // Only the undetermined state prompts; the picker opens regardless
        // of the answer. Web (no plugin) skips straight to the picker so the
        // same-tap transient activation still covers the programmatic click.
        if (isScreenshotSweepAvailable()) {
          try {
            await ensureScreenshotAccess();
          } catch {
            /* never let the permission probe block the picker */
          }
        }
        const { pickImages } = await import("@/lib/nativePicker");
        const picked = await pickImages(12);
        if (picked.length === 0) {
          onClose();
          return;
        }
        const files = picked.map(
          (p, i) =>
            new File([p.blob], `photo-${i + 1}.${p.mime.includes("png") ? "png" : "jpg"}`, {
              type: p.mime,
            }),
        );
        await handleFiles(files);
      })();
    } else if (heldReviewOnOpen) {
      // Review the background sweep's held screenshots: re-read each from
      // Photos by asset id, review-first (never auto). Assets deleted from
      // Photos since the sweep read as null → counted skipped.
      pickFiredRef.current = true;
      autoRef.current = false;
      const held = listHeldScreenshots();
      if (held.length === 0) {
        onClose();
        return;
      }
      void runPipeline(
        (async function* () {
          for (let i = 0; i < held.length; i++) {
            try {
              yield await readScreenshotFile(held[i], i);
            } catch {
              yield null;
            }
          }
        })(),
        held.length,
      );
    }
    // handleFiles/onClose are stable enough per open; keying on `open` alone
    // keeps this a strict once-per-open trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pickOnOpen, heldReviewOnOpen]);

  if (!open) return null;

  async function handleFiles(input: FileList | File[] | null) {
    const files = input ? Array.from(input) : [];
    if (files.length === 0) return;
    await runPipeline(
      (async function* () {
        for (const f of files) yield f;
      })(),
      files.length,
    );
  }

  /**
   * The scan pipeline over a LAZY stream of files. The picker path wraps its
   * array; the Auto Scan album sweep yields screenshots one at a time so a
   * 300-shot sweep never holds 300 blobs in memory at once. A `null` item
   * means the source could not read that photo — counted as skipped, never
   * fatal to the run.
   */
  async function runPipeline(source: AsyncIterable<File | null>, total: number) {
    setPhase("running");
    setProgress({ done: 0, total });
    const out: Pending[] = [];
    let i = -1;
    try {
      for await (const file of source) {
        i++;
        if (!file) {
          setSkippedPhotos((n) => n + 1);
          setProgress({ done: i + 1, total });
          continue;
        }
        const res = await classifyScreenshot(file, { initialVariant, isPro });
        if (res.kind === "skip") {
          setSkippedPhotos((n) => n + 1);
        } else {
          out.push(res.pending);
        }
        setProgress({ done: i + 1, total });
      }

    } catch (err) {
      console.warn("[ScanPokedexModal] OCR failed", err);
      toast({ title: "Could not read those screenshots", description: "Try again with a Pokédex or Pokémon screen open." });
      setPhase("pick");
      return;
    }
    setPending(out);
    // Nothing usable at all: back to pick with an honest explanation, rather
    // than an empty review screen.
    if (out.length === 0) {
      toast({
        title: "No Pokémon GO screens found",
        description: "None of those photos looked like a GO Pokédex, detail page or caught Pokémon.",
      });
      setPhase("pick");
      return;
    }
    // Auto Scan: save every row that parsed cleanly (variant pinned, nothing
    // locked or unresolved) in one go, and hold ONLY the rows that need a
    // human decision in review. It used to drop the ENTIRE batch to review
    // when one row was uncertain, which on a camera-roll pick meant auto
    // effectively never auto-saved — the opposite of what the footnote
    // promises. Auto still never guesses: uncertain rows always stop.
    if (autoRef.current) {
      const humanRows = out.filter(needsHumanReview);
      const cleanRows = out.filter((p) => !needsHumanReview(p));
      if (cleanRows.length > 0) {
        await confirm(cleanRows, humanRows);
        return;
      }
      toast({
        title: "These screenshots need you",
        description: "Auto Scan saves nothing it isn't sure of — confirm the rows below.",
      });
    }
    setPhase("review");
  }

  /**
   * Auto Scan on iOS (2026-08-27): sweep the Screenshots album directly —
   * no manual pick. Pressing Auto Scan IS the trainer's act of choosing to
   * share, so the first press asks for photo access; after that each run
   * processes only screenshots newer than the last sweep (device-local
   * cursor), streamed one at a time through the normal pipeline. Denied
   * access falls back to the picker flow, never a dead end.
   */
  async function runAlbumSweep() {
    autoRef.current = true;
    const status = await ensureScreenshotAccess();
    if (status === "denied" || status === "restricted") {
      toast(
        status === "restricted"
          ? {
              // Screen Time / parental controls: no prompt ever fires and
              // Settings has no Photos row, so don't send people there.
              title: "Photos access is blocked on this device",
              description:
                "Screen Time or parental controls block photo library access, so Auto Scan can't run. Picking screenshots by hand still works.",
            }
          : {
              title: "Photos access is off",
              description:
                "Allow photo access in Settings for automatic sweeps. Picking screenshots by hand still works.",
            },
      );
      const { pickImages } = await import("@/lib/nativePicker");
      const picked = await pickImages(12);
      if (picked.length === 0) return;
      await handleFiles(
        picked.map(
          (p, i) =>
            new File([p.blob], `photo-${i + 1}.${p.mime.includes("png") ? "png" : "jpg"}`, {
              type: p.mime,
            }),
        ),
      );
      return;
    }
    setPhase("running");
    setProgress({ done: 0, total: 0 });
    let plan;
    try {
      plan = await planSweep();
    } catch (err) {
      console.warn("[ScanPokedexModal] album sweep failed to list", err);
      toast({ title: "Could not read your Screenshots album", description: "Try again, or choose screenshots by hand." });
      setPhase("pick");
      return;
    }
    if (plan.items.length === 0) {
      toast({
        title: "You're up to date",
        description: "No new screenshots since your last Auto Scan.",
      });
      setPhase("pick");
      return;
    }
    const items = plan.items;
    await runPipeline(
      (async function* () {
        for (let i = 0; i < items.length; i++) {
          let file: File | null = null;
          try {
            file = await readScreenshotFile(items[i], i);
          } catch (err) {
            console.warn("[ScanPokedexModal] could not read screenshot", err);
          }
          yield file;
          // The pipeline pulled the next item, so this one is fully
          // processed: safe to advance the cursor past it.
          setSweepCursor(items[i].createdAt);
        }
      })(),
      items.length,
    );
  }

  // User explicitly confirms the Dex variant for a row the classifier could not
  // read. This is the ONLY way an unresolved row acquires a variant — there is
  // no silent fallback.
  function setPendingVariant(index: number, variant: PokedexVariant) {
    setPending((prev) =>
      prev.map((p, i) =>
        i === index && p.kind === "grid" ? { ...p, variant } : p,
      ),
    );
  }

  // Block saving while any GRID row still lacks a confirmed variant, so an
  // unidentified screenshot can never write to the wrong Dex. Detail rows carry
  // their own per-variant states and never need a variant picker.
  const hasUnconfirmed = pending.some(
    (p) => p.kind === "grid" && p.variant == null,
  );
  // Detail rows the uploader can't save yet because they lack Pro.
  const hasLockedDetail = pending.some((p) => p.kind === "detail" && p.locked);
  // At least one row we can actually save.
  const hasSavable = pending.some(
    (p) =>
      (p.kind === "grid" && p.variant != null) ||
      (p.kind === "detail" && !p.locked) ||
      (p.kind === "myPokemon" && !p.unresolved),
  );

  // Saves `rows` (the reviewed pending list by default; Auto Scan passes its
  // freshly-parsed batch directly, before the pending state has committed).
  // `remaining` is Auto Scan's needs-a-human rows: after the clean rows save,
  // the modal drops to review holding ONLY those, instead of ending.
  async function confirm(rows: Pending[] = pending, remaining: Pending[] = []) {
    setPhase("uploading");
    // The classify + save core lives in screenshotScanPipeline (shared with
    // the background Auto Scan runner); the modal owns phases and toasts.
    const { saved, rejected, dexStateApplied, myPokemonSaved, detailFeedbacks } =
      await saveScreenshotRows(rows);
    // My Pokémon rows get their own confirmation — they landed in a different
    // database than everything else in this modal, and the toast says where.
    if (myPokemonSaved > 0) {
      toast({
        title: `${myPokemonSaved} saved to My Pokémon`,
        description: "CP, IVs and moves recorded. Open My Pokémon to see them.",
      });
    }
    // Detail scans get the dedicated two-line confirmation ("<Name> (####)" /
    // form-or-dex line); a single one shows verbatim, several roll up.
    if (detailFeedbacks.length === 1) {
      toast({
        title: detailFeedbacks[0].line1,
        description: detailFeedbacks[0].line2,
      });
    } else if (detailFeedbacks.length > 1) {
      // Honest roll-up: report what actually landed in the dex. `dexStateApplied`
      // is 0 when every read variant was already on file (or blocked), so the
      // title never claims a change that didn't happen.
      toast({
        title: `${detailFeedbacks.length} Pokémon scanned`,
        description:
          dexStateApplied > 0
            ? `${dexStateApplied} ${dexStateApplied === 1 ? "dex entry" : "dex entries"} updated`
            : "Already up to date",
      });
    } else {
      toast({
        title: rejected === 0 ? "Pokédex saved" : "Saved with some warnings",
        description: `${dexStateApplied} entries updated, ${saved} scan${saved === 1 ? "" : "s"} synced${rejected > 0 ? `, ${rejected} skipped` : ""}.`,
      });
    }
    // (Dex totals are committed inside saveScreenshotRows, at the write.)
    if (remaining.length > 0) {
      // Auto Scan saved the certain rows; the uncertain ones still get their
      // human. The toast explains why the modal is showing a shorter list.
      setPending(remaining);
      setPhase("review");
      toast({
        title: `${remaining.length} screenshot${remaining.length === 1 ? "" : "s"} need${remaining.length === 1 ? "s" : ""} you`,
        description: "Everything certain is already saved — confirm the rows below.",
      });
    } else if (pickOnOpen || heldReviewOnOpen) {
      // Picker-on-open flows: the toast above already says what saved — close
      // instead of parking on an "All done" screen that costs one more tap.
      // A saved held-review clears the queue (those screenshots got their
      // human decision, whichever way it went).
      if (heldReviewOnOpen) clearHeldScreenshots();
      onClose();
    } else {
      setPhase("done");
    }
    onConfirm?.();
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end md:items-center justify-center bg-black/75 backdrop-blur-sm"
      onClick={() => phase !== "uploading" && onClose()}
      role="dialog"
      aria-label="Scan Pokédex"
    >
      {/* Same chrome as DexLiveScanMenu (2026-08-21 ask): rounded sheet,
          glow border, livescan eyebrow/title tokens — the two scan surfaces
          should read as one family. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full md:max-w-md v2-neon-card p-5 max-h-[90dvh] overflow-y-auto rounded-t-3xl md:rounded-3xl"
        style={{ background: "var(--surface-dark-7)", border: "1px solid var(--accent-fill-glow)" }}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.22em] font-bold"
              style={{ color: "var(--livescan-eyebrow)" }}
            >
              SCAN POKÉDEX
            </p>
            <h3 className="text-lg font-extrabold mt-1" style={{ color: "var(--livescan-title)" }}>
              {phase === "pick" && "Add screenshots"}
              {phase === "running" && "Reading screenshots..."}
              {phase === "review" && "Confirm what we read"}
              {phase === "uploading" && "Saving..."}
              {phase === "done" && "All done"}
            </h3>
            {phase === "pick" && (
              <p className="text-[11px] mt-1" style={{ color: "var(--livescan-text-muted)" }}>
                Pokédex grids, detail pages, or your own Pokémon.
              </p>
            )}
          </div>
          <button
            onClick={() => phase !== "uploading" && onClose()}
            aria-label="Close"
            data-testid="scan-pokedex-close"
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow)", color: "var(--livescan-text)" }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {phase === "pick" && (
          <>
            {/* Copy CUT RIGHT DOWN (2026-08-21 ask) — one line of guidance,
                one short Pro hint, then the two actions in the live-scan
                menu's row style. */}
            <p className="text-[12px] mb-2 leading-snug" style={{ color: "var(--livescan-text-muted)" }}>
              Two kinds of screenshot work: Pokédex screens (one per scroll)
              fill your dex, and a Pokémon's own page (CP, IVs, moves) fills
              My Pokémon.
              {initialVariant && (
                <>
                  {" "}Saving into the{" "}
                  <strong style={{ color: "var(--livescan-title)" }}>
                    {initialVariant.toUpperCase()}
                  </strong>{" "}
                  Dex.
                </>
              )}
            </p>
            <p
              className="text-[11px] mb-3 flex items-start gap-1.5"
              style={{ color: "var(--livescan-text-muted)" }}
              data-testid="scan-pokedex-detail-hint"
            >
              <Crown className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--accent-fill)" }} />
              <span>
                <strong style={{ color: "var(--livescan-title)" }}>Pro:</strong> one
                detail-page shot updates every dex and form that Pokémon has.
              </span>
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              data-testid="scan-pokedex-files"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <div className="flex flex-col gap-2.5">
              <button
                onClick={() => {
                  // Sweep availability is a SYNC check so the web fallback's
                  // file-input click keeps its user-activation.
                  if (isScreenshotSweepAvailable()) {
                    void runAlbumSweep();
                    return;
                  }
                  autoRef.current = true;
                  fileRef.current?.click();
                }}
                data-testid="scan-pokedex-auto"
                className="w-full rounded-2xl py-3 inline-flex items-center justify-between gap-3 px-4"
                style={{
                  background: "var(--accent-fill-glow-faint)",
                  border: "1px solid var(--accent-fill-glow)",
                  color: "var(--livescan-text)",
                  minHeight: 56,
                }}
              >
                <span className="inline-flex items-center gap-2 text-sm font-bold">
                  <Zap className="w-4 h-4" />
                  Auto Scan
                </span>
                <span
                  className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
                  style={{
                    background: "hsl(43 96% 60% / 0.12)",
                    color: "var(--livescan-badge)",
                    border: "1px solid var(--accent-fill-glow)",
                  }}
                >
                  Mass import
                </span>
              </button>
              <button
                onClick={() => {
                  autoRef.current = false;
                  fileRef.current?.click();
                }}
                data-testid="scan-pokedex-choose"
                className="w-full rounded-2xl py-3 inline-flex items-center justify-between gap-3 px-4"
                style={{
                  background: "var(--surface-dark-9)",
                  border: "1px solid var(--accent-fill-glow)",
                  color: "var(--livescan-text)",
                  minHeight: 56,
                }}
              >
                <span className="inline-flex items-center gap-2 text-sm font-bold">
                  <Upload className="w-4 h-4" />
                  Choose screenshots
                </span>
                <span
                  className="text-[10px] uppercase tracking-wider font-bold"
                  style={{ color: "var(--livescan-badge)" }}
                >
                  Review first
                </span>
              </button>
            </div>
            <p
              className="text-[10px] mt-3 leading-relaxed"
              style={{ color: "var(--livescan-text-muted)" }}
              data-testid="scan-pokedex-footnote"
            >
              {isScreenshotSweepAvailable()
                ? "Auto Scan sweeps your Screenshots album by itself: it finds new Pokémon GO screens, skips everything else, and saves every clean read in one go. Anything uncertain still asks you first."
                : "Auto Scan can take your whole camera roll: it finds the Pokémon GO screens, skips everything else, and saves every clean read in one go. Anything uncertain still asks you first."}
            </p>
          </>
        )}

        {phase === "running" && (
          <p className="text-[12px]" style={{ color: "var(--text-ink-soft)" }}>
            {progress.done} / {progress.total} read so far...
          </p>
        )}

        {phase === "review" && (
          <>
            <ul className="space-y-2 mb-3">
              {pending.map((p, i) => (
                <li
                  key={i}
                  className="rounded-xl p-3"
                  style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow-subtle)" }}
                  data-testid={`scan-pokedex-row-${i}`}
                >
                  {p.kind === "myPokemon" ? (
                    <MyPokemonReviewBody p={p} />
                  ) : p.kind === "detail" ? (
                    <DetailReviewBody p={p} />
                  ) : (
                  <>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--accent-fill)" }}>
                      {p.frame.activeRegion === "unknown" ? "Region?" : capitalize(p.frame.activeRegion)}
                    </span>
                    {p.variant != null && !p.needsConfirmation && (
                      <span
                        className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
                        style={{
                          background: isSparseVariant(p.variant)
                            ? "color-mix(in srgb, hsl(270 60% 55%) 16%, transparent)"
                            : "var(--accent-fill-glow-faint)",
                          color: isSparseVariant(p.variant)
                            ? "hsl(270 55% 55%)"
                            : "var(--text-ink-bright)",
                          border: isSparseVariant(p.variant)
                            ? "1px solid color-mix(in srgb, hsl(270 60% 55%) 45%, transparent)"
                            : "1px solid var(--accent-fill-glow)",
                        }}
                      >
                        {p.variant}
                      </span>
                    )}
                    {p.variant != null && !p.needsConfirmation && isSparseVariant(p.variant) && (
                      <span
                        className="text-[9px]"
                        style={{ color: "hsl(270 55% 55%)" }}
                        title="Sparse Dex: only visible entries are ingested"
                      >
                        sparse
                      </span>
                    )}
                    {p.frame.warnings.length > 0 && (
                      <AlertTriangle className="w-3 h-3" style={{ color: "hsl(43 96% 45%)" }} />
                    )}
                  </div>
                  {p.needsConfirmation && (
                    <div
                      className="rounded-lg p-2 mb-2"
                      style={{ background: "color-mix(in srgb, hsl(43 96% 55%) 16%, transparent)", border: "1px solid hsl(43 96% 60% / 0.45)" }}
                    >
                      <p className="text-[11px] font-bold mb-1 flex items-center gap-1" style={{ color: "hsl(43 80% 40%)" }}>
                        <AlertTriangle className="w-3 h-3" /> Dex type not recognised
                      </p>
                      <p className="text-[10px] mb-2" style={{ color: "var(--text-ink-soft)" }}>
                        We couldn&apos;t read the Dex type from this screenshot. Choose it so we don&apos;t save to the wrong Dex.
                      </p>
                      <select
                        data-testid={`scan-pokedex-variant-select-${i}`}
                        className="w-full text-[12px] rounded-md px-2 py-2"
                        style={{ background: "var(--surface-dark-7)", color: "var(--text-ink-bright)", border: "1px solid var(--accent-fill-glow)" }}
                        value={p.variant ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v) setPendingVariant(i, v as PokedexVariant);
                        }}
                      >
                        <option value="">Select Dex type…</option>
                        {ALL_VARIANTS.map((v) => (
                          <option key={v} value={v}>
                            {VARIANT_PICKER_LABELS[v]}
                            {p.suggested === v ? " (current tab)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <p className="text-[14px] font-bold" style={{ color: "var(--text-ink-bright)" }} data-testid={`scan-pokedex-progress-${i}`}>
                    {formatScanProgress({
                      progressSeen: p.frame.progressSeen,
                      progressTotal: p.frame.progressTotal,
                      cellsRead: p.frame.cells.length,
                      needsConfirmation: p.needsConfirmation,
                    })}
                  </p>
                  <p className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>
                    Confidence {p.frame.confidence}%. {p.frame.cells.length} cell{p.frame.cells.length === 1 ? "" : "s"} read.
                  </p>
                  </>
                  )}
                </li>
              ))}
            </ul>
            <button
              onClick={() => void confirm()}
              disabled={hasUnconfirmed || !hasSavable}
              data-testid="scan-pokedex-confirm"
              className="v2-neon-cta w-full py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" /> Save to my Pokédex
            </button>
            {hasUnconfirmed && (
              <p className="text-[10px] mt-2 text-center" style={{ color: "hsl(43 80% 40%)" }}>
                Choose a Dex type for each unrecognised screenshot to continue.
              </p>
            )}
            {!hasUnconfirmed && !hasSavable && hasLockedDetail && (
              <p className="text-[10px] mt-2 text-center" style={{ color: "hsl(43 80% 40%)" }}>
                Detail-page scanning is a Pro feature. Upgrade above to save these.
              </p>
            )}
          </>
        )}

        {phase === "uploading" && (
          <p className="text-[12px]" style={{ color: "var(--text-ink-soft)" }}>Saving your Pokédex progress...</p>
        )}

        {phase === "done" && (
          <>
            {skippedPhotos > 0 && (
              <p
                className="text-[11px] mb-3"
                style={{ color: "var(--livescan-text-muted)" }}
                data-testid="scan-pokedex-skipped"
              >
                {skippedPhotos} photo{skippedPhotos === 1 ? " wasn't" : "s weren't"} a
                Pokémon GO screen and {skippedPhotos === 1 ? "was" : "were"} skipped.
              </p>
            )}
            <button
              onClick={onClose}
              className="v2-neon-cta w-full py-3"
              data-testid="scan-pokedex-done"
            >
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Compact chip labels for the detail-page variant summary.
const DETAIL_VARIANT_LABEL: Partial<Record<PokedexVariant, string>> = {
  pokemon: "Caught",
  shiny: "Shiny",
  lucky: "Lucky",
  perfect: "100%",
  xxs: "XXS",
  xxl: "XXL",
  mega: "Mega",
  gmax: "G-Max",
  dynamax: "D-MAX",
  shadow: "Shadow",
  purified: "Purified",
};

// Friendly text for the warning codes the detail parser emits.
function detailWarningText(code: string | undefined): string | null {
  switch (code) {
    case "tinted_view":
      return "This page was tinted by a selected dex tab, so some variant cards may be skipped. For a full read, tap the main sprite (default view) before scanning.";
    case "no_variant_cards":
      return "No variant cards were readable in this screenshot.";
    case "default_card_ungeolocated":
      return "Couldn't place the main card — scroll so the top row is fully visible and re-scan.";
    default:
      return null;
  }
}

/**
 * Review body for a Pokémon detail-page row: the species header, a Pro badge,
 * and either the ProLock upsell (non-Pro) or the per-variant present/not-yet
 * summary we will save.
 */
/** Review body for an individual caught-Pokémon screenshot: what will land in
 * My Pokémon, or why it can't yet (a nickname needs the appraisal shot). */
function MyPokemonReviewBody({ p }: { p: MyPokemonPending }) {
  const f = p.frame;
  const name = f.nickname ?? f.speciesName ?? f.displayName ?? "Unknown";
  return (
    <div data-testid="scan-my-pokemon-review">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--accent-fill)" }}>
          My Pokémon
        </span>
        {p.unresolved && (
          <AlertTriangle className="w-3 h-3" style={{ color: "hsl(43 96% 45%)" }} />
        )}
      </div>
      <p className="text-[14px] font-bold" style={{ color: "var(--text-ink-bright)" }}>
        {name}
        {f.speciesId != null && (
          <span className="ml-1.5 text-[11px] font-semibold" style={{ color: "var(--text-ink-soft)" }}>
            #{f.speciesId}
          </span>
        )}
      </p>
      <p className="text-[11px] mt-0.5" style={{ color: "var(--text-ink-soft)" }}>
        {[
          f.cp != null ? `CP ${f.cp}` : null,
          f.hpMax != null ? `${f.hpMax} HP` : null,
          f.iv ? `IV ${f.iv.atk}/${f.iv.def}/${f.iv.hp}` : null,
          f.moves.length > 0 ? `${f.moves.length} move${f.moves.length === 1 ? "" : "s"}` : null,
        ]
          .filter(Boolean)
          .join(" · ") || "Nothing readable on this shot"}
      </p>
      {p.unresolved && (
        <p className="text-[10px] mt-1" style={{ color: "hsl(43 80% 40%)" }}>
          Nicknamed — upload its APPRAISAL screenshot too, which names the
          species ("This Slaking was caught…"). Skipped otherwise.
        </p>
      )}
    </div>
  );
}

function DetailReviewBody({ p }: { p: DetailPending }) {
  const frame = p.frame;
  // What SAVING does, not what the screenshot shows — see previewDetailFrame.
  const { caught, toUpdate } = previewDetailFrame(frame);
  const warning = detailWarningText(frame.warnings[0]);
  return (
    <>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--accent-fill)" }} />
        <span
          className="text-[13px] font-extrabold"
          style={{ color: "var(--text-ink-bright)" }}
          data-testid="scan-detail-title"
        >
          {dexNumberLabel(frame.dexNumber ?? 0)}{" "}
          {frame.name
            ? frame.name.charAt(0) + frame.name.slice(1).toLowerCase()
            : frame.dexNumber
              ? speciesName(frame.dexNumber)
              : ""}
        </span>
        {frame.formLabel && (
          <span
            className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
            style={{ background: "color-mix(in srgb, hsl(200 70% 50%) 18%, transparent)", color: "hsl(200 70% 40%)", border: "1px solid color-mix(in srgb, hsl(200 70% 50%) 50%, transparent)" }}
          >
            {frame.formLabel}
          </span>
        )}
        <span
          className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
          style={{ background: "var(--accent-fill-glow-faint)", color: "var(--accent-fill)", border: "1px solid var(--accent-fill-glow)" }}
        >
          <Crown className="w-2.5 h-2.5" /> Pro
        </span>
      </div>

      {p.locked ? (
        <ProLock
          feature="detail-scan"
          source="detail-scan"
          blurb="Scan a Pokémon's detail page and update every dex it has (Shiny, Lucky, 100%, XXL, XXS, Shadow, Purified and more) from one screenshot."
        />
      ) : (
        <>
          {frame.formSelectorPresent && frame.forms.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="scan-detail-forms">
              <span className="text-[9px] uppercase tracking-wider" style={{ color: "hsl(200 45% 50%)" }}>
                Forms
              </span>
              {frame.forms.map((f) => {
                const present = f.state === "present";
                return (
                  <span
                    key={f.formId ?? "base"}
                    className="text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                    style={{
                      background: present ? "color-mix(in srgb, hsl(200 70% 50%) 22%, transparent)" : "var(--surface-sunken)",
                      color: present ? "hsl(200 80% 88%)" : "var(--text-ink-soft)",
                      border: f.selected
                        ? "1px solid var(--accent-fill-glow-strong)"
                        : present
                          ? "1px solid color-mix(in srgb, hsl(200 70% 50%) 50%, transparent)"
                          : "1px dashed var(--border-subtle)",
                    }}
                    title={f.selected ? "Currently-shown form" : undefined}
                  >
                    {f.label} {present ? "✓" : "-"}
                    {f.selected ? " ●" : ""}
                  </span>
                );
              })}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 my-1.5" data-testid="scan-detail-variants">
            {frame.variants.map((v) => {
              const present = v.state === "present";
              return (
                <span
                  key={`${v.variant}:${v.label}`}
                  className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{
                    background: present ? "var(--accent-fill-glow-faint)" : "var(--surface-sunken)",
                    color: present ? "var(--text-ink-bright)" : "var(--text-ink-soft)",
                    border: present
                      ? "1px solid var(--accent-fill-glow)"
                      : "1px dashed var(--border-subtle)",
                  }}
                >
                  {DETAIL_VARIANT_LABEL[v.variant] ?? v.variant} {present ? "✓" : "-"}
                </span>
              );
            })}
          </div>
          <p className="text-[10px]" style={{ color: "var(--text-tertiary)" }} data-testid="scan-detail-counts">
            {caught} caught · {toUpdate === 0 ? "nothing to update" : `${toUpdate} to be updated`} ·
            {" "}Confidence {frame.confidence}%
          </p>
          {warning && (
            <p
              className="text-[10px] mt-1 flex items-start gap-1"
              style={{ color: "hsl(43 80% 40%)" }}
            >
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {warning}
            </p>
          )}
        </>
      )}
    </>
  );
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export type { PokedexRegion, PokedexFilter };
